import React from 'react'
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useEffect } from 'react';
import { encrypt } from '../../../Utils/hmac';
import { simmetryImageUpload } from '../../../Services/APIs/scan';
import { submitSimmetryScan } from '../../../Services/APIs/scan';
import { getCroppedImg } from '../../../Utils/cropImageHelper';
import { useCallback } from 'react';
import InvalidImage from '../ReshootError/invalidImage';
import ServerError from '../ReshootError/serverError';
import AboutMeForm from '../Form/AboutMeForm';
import Loader from '../Result/loader';
import ServerBusyPopup from './serverBusyPopup';
import { getLocalStorageJSON } from '../../../Utils/safeLocalStorage';
import { logEvent } from '../../../Utils/logEvent';

const PIPELINE_STATUS = {
    CROPPING: 'cropping',
    IDLE: 'idle',
    UPLOADING: 'uploading',
    RESHOOT: 'reshoot',
    BUSY: 'busy',
    ERROR: 'error',
    SCANNING: 'scanning',
    SUCCEEDED: 'succeeded',
};

const Preview = (props) => {

    const company = localStorage.getItem("company");
    const uid = getLocalStorageJSON("usertpi").usertpi || "";

    const { image, setCamera, setDemo } = props;
    const navigate = useNavigate();
    const [croppedBlob, setCroppedBlob] = useState();
    const [pipelineStatus, setPipelineStatus] = useState(PIPELINE_STATUS.CROPPING);
    const [isFormSubmitted, setIsFormSubmitted] = useState(() => {
        return localStorage.getItem("is_form") === "true";
    });

    const showAboutMeForm = !isFormSubmitted && (pipelineStatus === PIPELINE_STATUS.SCANNING || pipelineStatus === PIPELINE_STATUS.SUCCEEDED);

    const toggleActiveState = () => {
        localStorage.setItem("is_form", "true");
        setIsFormSubmitted(true);
    };

    useEffect(() => {
        if (pipelineStatus === PIPELINE_STATUS.SUCCEEDED && isFormSubmitted) {
            localStorage.setItem("isScanCompleted", "true")
            navigate('/claraview/result', { replace: true });
        }
    }, [pipelineStatus, isFormSubmitted]);

    const uploadImage = async (formData) => {
        try {
            return await simmetryImageUpload({ formData });
        } catch (err) {
            const status = err?.response?.status;
            logEvent({
                app: company, uid,
                event: status === 429 ? "image upload busy (429)" : "image upload failed",
                error: err
            });
            throw err;
        }
    };

    const scanImage = async (info) => {
        try {
            return await submitSimmetryScan(info);
        } catch (err) {
            const status = err?.response?.status;
            logEvent({
                app: company, uid,
                event: status === 429 ? "scan busy (429)" : "scan failed",
                error: err
            });
            throw err;
        }
    };

    const submit = async () => {
        if (!uid || !image) {
            return;
        }

        const data = { uid, folderName: uid, company };
        const encrypted = encrypt(data);
        const file = new File([croppedBlob], "report_image.jpeg", { type: "image/jpeg", });

        const formData = new FormData();
        formData.append("report_image", file);
        formData.append("_n", encrypted);

        setPipelineStatus(PIPELINE_STATUS.UPLOADING);

        try {
            const uploadResponse = await uploadImage(formData);
            if (uploadResponse.data?.data?.is_reshoot) {
                logEvent({ app: company, uid, event: "image upload reshoot" });
                setPipelineStatus(PIPELINE_STATUS.RESHOOT);
                return;
            }

            const info = {
                uid,
                folderName: uid,
                company,
                imageName: uploadResponse.data.data.imageName
            };
            localStorage.setItem("response", JSON.stringify(uploadResponse.data.data));
            setPipelineStatus(PIPELINE_STATUS.SCANNING);

            const scanResponse = await scanImage(info);
            const resData = scanResponse?.data?.data;

            if (resData?.is_server_busy) {
                logEvent({ app: company, uid, event: "scan busy (flag)" });
                setPipelineStatus(PIPELINE_STATUS.BUSY);
                return;
            }
            if (resData?.is_reshoot) {
                logEvent({ app: company, uid, event: "scan reshoot" });
                setPipelineStatus(PIPELINE_STATUS.RESHOOT);
                return;
            }

            logEvent({ app: company, uid, event: "scan succeeded" });
            setPipelineStatus(PIPELINE_STATUS.SUCCEEDED);
        } catch (err) {
            const status = err?.response?.status;
            setPipelineStatus(status === 429 ? PIPELINE_STATUS.BUSY : PIPELINE_STATUS.ERROR);
        }
    };

    const showCroppedImage = useCallback(async () => {
        try {
            const croppedImgBlob = await getCroppedImg(image);
            setCroppedBlob(croppedImgBlob);
            setPipelineStatus(PIPELINE_STATUS.IDLE);
        } catch (error) {
            logEvent({ app: company, uid, event: "image crop failed", error: error });
            setPipelineStatus(PIPELINE_STATUS.ERROR);
        }
    }, [image]);

    useEffect(() => {
        showCroppedImage();
    }, []);


    return (
        <div className="h-full w-full flex flex-col justify-between items-center font-novatica py-10">
            {
                showAboutMeForm &&
                <AboutMeForm toggleActive={toggleActiveState} />
            } {
                pipelineStatus === PIPELINE_STATUS.IDLE &&
                <>

                    <p className='text-[18px] text-[#022022] font-medium'>Preview</p>
                    <img src={image} alt="" className='w-4/5 rounded-lg h-3/5 max-h-3/5 object-contain' />
                    <div className="flex w-full justify-center items-center flex-col gap-4">
                        <button
                            onClick={submit}
                            className=' font-medium bg-[#2C2D24] text-white w-40 text-[17px]  px-5 rounded-md py-3'>Continue</button>
                        <button
                            onClick={setCamera}
                            className=' font-medium bg-[#C3D8CC] text-[#2C2D24] w-40 text-[17px] px-5 rounded-md py-3	'>Retake</button>
                    </div>
                </>
            }
            {
                [PIPELINE_STATUS.CROPPING, PIPELINE_STATUS.UPLOADING, PIPELINE_STATUS.SCANNING].includes(pipelineStatus) && !showAboutMeForm &&
                <Loader text={"Evaluating your image..."} />
            }
            {
                pipelineStatus === PIPELINE_STATUS.RESHOOT &&
                <InvalidImage setCamera={setCamera} setDemo={setDemo} onClose={() => setPipelineStatus(PIPELINE_STATUS.IDLE)} />
            }
            {
                pipelineStatus === PIPELINE_STATUS.ERROR &&
                <ServerError setCamera={setCamera} onClose={() => setPipelineStatus(PIPELINE_STATUS.IDLE)} />
            }
            {
                pipelineStatus === PIPELINE_STATUS.BUSY &&
                <ServerBusyPopup onClose={(shouldRetry) => shouldRetry ? submit() : setPipelineStatus(PIPELINE_STATUS.IDLE)} />
            }
        </div>
    )
}

export default Preview
